import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState } from 'react';
import { Check, Copy, TriangleAlert } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface NewKeyRevealModalProps {
  /** O segredo em claro. Só existe aqui — a partir do fechamento, some. */
  apiKey: string | null;
  keyName: string;
  onClose: () => void;
}

/**
 * Exibe a chave recém-criada.
 *
 * É a única tela do sistema que mostra o segredo: o backend guarda apenas o
 * hash, então nem o suporte consegue recuperá-lo depois. Por isso o modal não
 * fecha ao clicar fora nem no Esc — fechar sem copiar significa gerar outra
 * chave, e o usuário precisa tomar essa decisão de propósito.
 */
export function NewKeyRevealModal({ apiKey, keyName, onClose }: NewKeyRevealModalProps) {
  useLanguage();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!apiKey) return;

    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      toast.success(translateInterfaceText("Chave copiada"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(translateInterfaceText("Não foi possível copiar. Selecione o texto e copie manualmente."));
    }
  };

  return (
    <Dialog
      open={!!apiKey}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/*
        hideCloseButton junto com os dois preventDefault: são as TRÊS saídas do
        Dialog. Sem esconder o X, fechar sem copiar continua a um clique de
        distância — no canto superior direito, exatamente onde a mão vai por
        reflexo — e o segredo deixa de existir.
      */}
      <DialogContent
        className="sm:max-w-[560px] rounded-xl"
        hideCloseButton
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-foreground">
            {translateInterfaceText("Chave criada")}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1.5">
            {translateInterfaceText("Copie agora. Por segurança, não guardamos a chave — só uma impressão digital dela, que não tem volta. Esta é a única vez que a chave \"")}{keyName}{translateInterfaceText("\" aparece.")}</DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div className="rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] p-3">
            <code className="block font-mono text-[13px] leading-relaxed text-[#111] break-all select-all">
              {apiKey}
            </code>
          </div>

          <Button
            onClick={handleCopy}
            className="w-full h-11 rounded-[10px] bg-[#437DFF] hover:bg-[#355FCC] font-semibold"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                {translateInterfaceText("Copiada")}</>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                {translateInterfaceText("Copiar chave")}</>
            )}
          </Button>

          <div className="flex gap-2.5 rounded-[10px] border border-amber-200 bg-amber-50 p-3">
            <TriangleAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[13px] leading-relaxed text-amber-900">
              {translateInterfaceText("Quem tem esta chave consegue ler as métricas dos seus vídeos. Guarde como guardaria uma senha e, se ela vazar, revogue na lista desta tela — a revogação vale na hora e não afeta suas outras chaves.")}</p>
          </div>

          <div className="rounded-[10px] border border-[#E2E8F0] p-3">
            <p className="text-[13px] font-semibold text-[#111] mb-1.5">{translateInterfaceText("Como usar")}</p>
            <p className="text-[13px] leading-relaxed text-[#64748B]">
              {translateInterfaceText("Ao adicionar o connector da HostVSL, cole a chave no campo de header")}{' '}
              <code className="font-mono text-[12px] bg-[#F1F5F9] px-1.5 py-0.5 rounded">
                X-API-Key
              </code>
              {translateInterfaceText(". Cole apenas o valor, sem escrever nada antes dele.")}</p>
          </div>
        </div>

        {/*
          O rótulo muda conforme o usuário já copiou ou não. Fechar continua
          sendo possível — obrigar a copiar prenderia quem desistiu no meio —,
          mas quem não copiou lê "sem copiar" antes de clicar, em vez de
          descobrir depois.
        */}
        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="h-10 px-4 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#1E293B]"
          >
            {copied ? translateInterfaceText("Já copiei, fechar") : translateInterfaceText("Fechar sem copiar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
